import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, LogIn, Heart, Sparkles } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_companions-hub-2/artifacts/xm3vjt0f_TCF_logo.jpg";
const HERO_IMAGE = "https://images.unsplash.com/photo-1657598329547-60f9d36b751f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtb3NxdWUlMjBhcmNoaXRlY3R1cmUlMjBzdW4lMjByYXlzfGVufDB8fHx8MTc3MTg2NjQ2OXww&ixlib=rb-4.1.0&q=85";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData, { withCredentials: true });
      login(response.data);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* Left Side - Hero Image */}
        <div className="hidden lg:block relative">
          <img 
            src={HERO_IMAGE} 
            alt="Modern mosque with sun rays" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-900/80 to-primary-900/60"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-12 xl:px-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm font-medium mb-6 w-fit">
              <Heart className="w-4 h-4" />
              Service to Humanity
            </div>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white mb-4 tracking-tight">
              Welcome Back to
              <span className="block text-primary-200">The Companions Foundation</span>
            </h1>
            <p className="text-lg text-white/80 leading-relaxed max-w-lg">
              Sign in to access your account and continue supporting our mission of serving the community through faith and compassion.
            </p>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="flex items-center justify-center px-4 py-12 lg:px-8">
          <div className="w-full max-w-md">
            <Link 
              to="/" 
              className="inline-flex items-center text-gray-600 hover:text-primary-900 mb-8 transition-colors group"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Link>

            <Card className="p-8 rounded-3xl shadow-xl border-0 bg-white">
              <div className="text-center mb-8">
                <img src={LOGO_URL} alt="TCF Logo" className="w-20 h-20 rounded-full mx-auto mb-4 shadow-lg" />
                <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Welcome Back</h1>
                <p className="text-gray-600">Sign in to your account to continue</p>
              </div>

              {/* Google Login */}
              <Button 
                type="button"
                variant="outline" 
                className="w-full mb-6 h-12 rounded-xl border-2 hover:bg-gray-50 transition-all"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="relative mb-6">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-gray-500">
                  or sign in with email
                </span>
              </div>

              {/* Email/Password Login */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="pl-12 h-12 rounded-xl border-gray-200 focus:border-primary-500 focus:ring-primary-500"
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className="pl-12 h-12 rounded-xl border-gray-200 focus:border-primary-500 focus:ring-primary-500"
                      data-testid="login-password"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-xl bg-primary-900 hover:bg-primary-800 text-white font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center text-gray-600 mt-8">
                Don't have an account?{" "}
                <Link to="/register" className="text-primary-900 hover:text-primary-700 font-semibold transition-colors">
                  Register here
                </Link>
              </p>
            </Card>

            {/* Mobile tagline */}
            <div className="lg:hidden text-center mt-8">
              <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4 text-primary-600" />
                Service to Humanity
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
